import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Search, Clock, AlertTriangle, ExternalLink, Archive, CheckCircle, Play } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/contatos")({
  component: AdminContatosPage,
});

function AdminContatosPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [assuntoFilter, setAssuntoFilter] = useState("todos");
  const [selectedContato, setSelectedContato] = useState<any>(null);
  const [notas, setNotas] = useState("");
  const queryClient = useQueryClient();

  const { data: contatos, isLoading } = useQuery({
    queryKey: ["admin-contatos"],
    queryFn: async () => {
      const { data } = await supabase
        .from("contatos" as any)
        .select("*, profiles:user_id(nome, email)")
        .order("created_at", { ascending: false });
      return (data || []) as any[];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, notas }: any) => {
      const { error } = await supabase.from("contatos" as any).update({ status, notas_internas: notas }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-contatos"] });
      toast.success("Status atualizado.");
      setSelectedContato(null);
    },
  });

  const filtered = contatos?.filter(c => {
    const matchesSearch = c.nome.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAssunto = assuntoFilter === "todos" || c.assunto === assuntoFilter;
    return matchesSearch && matchesAssunto;
  });

  const renderList = (status: string) => (
    <div className="space-y-4">
      {filtered?.filter(c => c.status === status).map(c => {
        const isRemocao = c.assunto === "solicitar_remoção";
        const diasRestantes = isRemocao ? 15 - differenceInDays(new Date(), new Date(c.created_at)) : null;

        return (
          <div 
            key={c.id} 
            onClick={() => { setSelectedContato(c); setNotas(c.notas_internas || ""); }}
            className={cn(
              "p-4 border rounded-xl cursor-pointer hover:shadow-md transition-all bg-white flex items-center gap-4",
              isRemocao && status === "novo" ? "border-red-200 bg-red-50/30" : ""
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-brand-dark truncate">{c.nome}</span>
                {isRemocao && status === "novo" && (
                  <Badge variant="destructive" className="text-[10px] py-0">URGENTE: REMOÇÃO</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-3">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>
                <span>{format(new Date(c.created_at), "dd/MM 'às' HH:mm")}</span>
              </div>
              <p className="text-sm text-brand-dark mt-2 line-clamp-1">{c.mensagem}</p>
            </div>
            <div className="text-right shrink-0">
              <Badge variant="outline" className="capitalize">{c.assunto.replace("_", " ")}</Badge>
              {isRemocao && status === "novo" && (
                <div className="text-[10px] text-red-600 font-bold mt-1">Limite: {diasRestantes} dias</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">Contatos</h1>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por nome ou e-mail..." 
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={assuntoFilter} onValueChange={setAssuntoFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os assuntos</SelectItem>
              <SelectItem value="dúvida_acesso">Dúvida Acesso</SelectItem>
              <SelectItem value="quero_anunciar">Anunciar</SelectItem>
              <SelectItem value="sugerir_fornecedor">Sugestão</SelectItem>
              <SelectItem value="reportar_erro">Erro</SelectItem>
              <SelectItem value="solicitar_remoção">Remoção</SelectItem>
              <SelectItem value="outro">Outro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="novo">
        <TabsList className="bg-white border rounded-xl p-1 mb-4 h-auto">
          <TabsTrigger value="novo" className="rounded-lg py-2">Novos ({filtered?.filter(c => c.status === "novo").length})</TabsTrigger>
          <TabsTrigger value="em_andamento" className="rounded-lg py-2">Em andamento ({filtered?.filter(c => c.status === "em_andamento").length})</TabsTrigger>
          <TabsTrigger value="respondido" className="rounded-lg py-2">Respondidos</TabsTrigger>
          <TabsTrigger value="arquivado" className="rounded-lg py-2">Arquivados</TabsTrigger>
        </TabsList>

        <TabsContent value="novo">{renderList("novo")}</TabsContent>
        <TabsContent value="em_andamento">{renderList("em_andamento")}</TabsContent>
        <TabsContent value="respondido">{renderList("respondido")}</TabsContent>
        <TabsContent value="arquivado">{renderList("arquivado")}</TabsContent>
      </Tabs>

      <Dialog open={!!selectedContato} onOpenChange={() => setSelectedContato(null)}>
        <DialogContent className="max-w-2xl rounded-2xl">
          {selectedContato && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <DialogTitle className="text-xl">{selectedContato.nome}</DialogTitle>
                  <Badge variant="outline" className="capitalize">{selectedContato.assunto.replace("_", " ")}</Badge>
                </div>
                {selectedContato.assunto === "solicitar_remoção" && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl border border-red-100 text-sm font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    Prazo legal de 15 dias (LGPD). Recebido em {format(new Date(selectedContato.created_at), "dd/MM/yyyy")}.
                  </div>
                )}
              </DialogHeader>
              <div className="space-y-6 my-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block mb-1">E-mail</span>
                    <a href={"mailto:" + selectedContato.email} className="font-semibold flex items-center gap-1 hover:text-primary">
                      {selectedContato.email} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div>
                    <span className="text-muted-foreground block mb-1">WhatsApp</span>
                    <span className="font-semibold">{selectedContato.telefone || "Não informado"}</span>
                  </div>
                  {selectedContato.profiles && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground block mb-1">Usuário Cadastrado</span>
                      <Link to={"/admin/usuarios?email=" + selectedContato.email} className="text-primary font-semibold flex items-center gap-1">
                        Ver perfil do usuário <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Mensagem</span>
                  <div className="p-4 bg-secondary rounded-xl text-sm whitespace-pre-wrap">{selectedContato.mensagem}</div>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Notas Internas</span>
                  <Textarea 
                    placeholder="Anotações para a equipe..." 
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    className="h-20"
                  />
                </div>
              </div>
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => {
                    const mailto = "mailto:" + selectedContato.email + "?subject=Re: GOPAGE - " + selectedContato.assunto + "&body=Olá " + selectedContato.nome + ",\n\nEm resposta à sua mensagem enviada em " + format(new Date(selectedContato.created_at), "dd/MM", { locale: ptBR }) + ":\n\n> " + selectedContato.mensagem;
                    window.location.href = mailto;
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" /> Responder por E-mail
                </Button>
                <div className="flex-1" />
                {selectedContato.status === "novo" && (
                  <Button onClick={() => updateStatus.mutate({ id: selectedContato.id, status: "em_andamento", notas })}>
                    <Play className="w-4 h-4 mr-2" /> Marcar em andamento
                  </Button>
                )}
                {selectedContato.status !== "respondido" && (
                  <Button onClick={() => updateStatus.mutate({ id: selectedContato.id, status: "respondido", notas })} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="w-4 h-4 mr-2" /> Marcar respondido
                  </Button>
                )}
                <Button variant="ghost" onClick={() => updateStatus.mutate({ id: selectedContato.id, status: "arquivado", notas })}>
                  <Archive className="w-4 h-4 mr-2" /> Arquivar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
